import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  CheckCircle2,
  Clock3,
  Mail,
  MailCheck,
  MailWarning,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  exportEmailLogs,
  fetchEmailLogDetail,
  fetchEmailLogStats,
  fetchEmailLogs,
  retryEmailLog,
} from "../../../Apis/EmailLogs-api";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import { hasPermissionAction } from "../../../Utils/organizationPermissions";

const TYPE_OPTIONS = [
  { value: "admission_welcome", label: "Admission Welcome" },
  { value: "fee_receipt", label: "Fee Receipt" },
  { value: "fee_due_reminder", label: "Fee Due Reminder" },
  { value: "fee_overdue", label: "Fee Overdue" },
  { value: "course_completion", label: "Course Completion" },
  { value: "certificate_issued", label: "Certificate Issued" },
  { value: "offer_to_lead", label: "Offer" },
  { value: "missed_followups", label: "Missed Follow-ups" },
  { value: "website_enquiry", label: "Website Enquiry" },
];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "delivered", label: "Delivered" },
  { value: "failed", label: "Failed" },
  { value: "bounced", label: "Bounced" },
];

const TRIGGER_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
  { value: "system", label: "System" },
];

const MODULE_OPTIONS = [
  { value: "admissions", label: "Admissions" },
  { value: "fees", label: "Fees" },
  { value: "batches", label: "Batches" },
  { value: "certificates", label: "Certificates" },
  { value: "offers", label: "Offers" },
  { value: "leads", label: "Leads" },
  { value: "followups", label: "Follow-ups" },
];

const dateFieldStyle = {
  height: 42,
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "0 12px",
  borderRadius: 12,
  border: "1px solid #d7dbe7",
  background: "#fff",
  boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
};

const dateInputStyle = {
  height: 42,
  border: "none",
  outline: "none",
  background: "transparent",
  color: "var(--ems-text, #1f2430)",
  fontSize: 13.5,
  fontFamily: "inherit",
};

function formatWhen(value) {
  if (!value) return { date: "—", time: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: String(value), time: "" };
  return {
    date: date.toLocaleDateString("en-GB"),
    time: date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }),
  };
}

function typeLabel(value) {
  return TYPE_OPTIONS.find((item) => item.value === value)?.label || value || "—";
}

function statusBadge(status) {
  if (status === "delivered" || status === "sent") return "green";
  if (status === "failed" || status === "bounced") return "red";
  if (status === "pending") return "amber";
  return "neutral";
}

function SelectFilter({ emptyLabel, value, onChange, options, minWidth = 180 }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = options.find((item) => item.value === value);
  const label = selected?.label || emptyLabel;

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const choices = [{ value: "", label: emptyLabel }, ...options];

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        style={{
          height: 42,
          minWidth,
          maxWidth: 280,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "0 14px",
          borderRadius: 12,
          border: open ? "1px solid var(--ems-primary, #0891b2)" : "1px solid #d7dbe7",
          background: "#fff",
          color: value ? "var(--ems-text, #1f2430)" : "var(--ems-muted, #6b7280)",
          fontSize: 13.5,
          fontFamily: "inherit",
          fontWeight: "inherit",
          boxShadow: open ? "0 0 0 3px rgba(8, 145, 178, 0.16)" : "0 1px 2px rgba(16, 24, 40, 0.06)",
          cursor: "pointer",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ color: "var(--ems-faint, #9095ab)", fontSize: 12, transform: open ? "rotate(180deg)" : "none" }}>▼</span>
      </button>
      {open ? (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: 48,
            left: 0,
            zIndex: 20,
            minWidth: Math.max(minWidth, 190),
            maxHeight: 280,
            overflowY: "auto",
            padding: 6,
            borderRadius: 14,
            border: "1px solid #e6e8f0",
            background: "#fff",
            boxShadow: "0 16px 40px rgba(16, 24, 40, 0.14)",
          }}
        >
          {choices.map((item) => {
            const active = item.value === value;
            return (
              <button
                key={item.value || item.label}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChange(item.value);
                  setOpen(false);
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 12px",
                  background: active ? "var(--ems-primary-tint, #e6f7fb)" : "transparent",
                  color: active ? "var(--ems-primary, #0891b2)" : "var(--ems-text, #1f2430)",
                  fontSize: 13.5,
                  fontFamily: "inherit",
                  fontWeight: "inherit",
                  cursor: "pointer",
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function EmailLogList() {
  const { userData } = useAuthStore();
  const Permission = usePermissionDataStore((state) => state.Permission);
  const org = userData?.organization;
  const canRetry = hasPermissionAction(Permission, "RETRY_EMAIL_LOGS");

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    delivered: 0,
    failed: 0,
    pending: 0,
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [emailType, setEmailType] = useState("");
  const [status, setStatus] = useState("");
  const [triggeredBy, setTriggeredBy] = useState("");
  const [relatedModule, setRelatedModule] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const filterParams = useMemo(
    () => ({
      organization_id: org?.organization_id,
      search: appliedSearch || undefined,
      email_type: emailType || undefined,
      status: status || undefined,
      triggered_by_type: triggeredBy || undefined,
      related_module: relatedModule || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [org?.organization_id, appliedSearch, emailType, status, triggeredBy, relatedModule, from, to]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setAppliedSearch(search.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!org?.organization_id) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [listRes, statsRes] = await Promise.all([
          fetchEmailLogs({ ...filterParams, page, per_page: 25 }),
          fetchEmailLogStats(filterParams),
        ]);
        if (cancelled) return;
        const payload = listRes?.email_logs || {};
        setRows(Array.isArray(payload.data) ? payload.data : []);
        setLastPage(payload.last_page || 1);
        setTotal(payload.total || 0);
        setStats(statsRes?.stats || { total: 0, sent: 0, delivered: 0, failed: 0, pending: 0 });
      } catch (error) {
        if (cancelled) return;
        setRows([]);
        toast.error(error?.response?.data?.error || "Failed to load email logs");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [filterParams, page, org?.organization_id]);

  const openDetail = async (row) => {
    if (!org?.organization_id) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const response = await fetchEmailLogDetail(row.email_log_id, org.organization_id);
      setDetail(response?.email_log || null);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load email details");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const onRetry = async (row) => {
    if (!org?.organization_id || !row?.retryable) return;
    setRetryingId(row.email_log_id);
    try {
      await retryEmailLog(row.email_log_id, org.organization_id);
      toast.success("Email retry sent");
      setPage(1);
      const [listRes, statsRes] = await Promise.all([
        fetchEmailLogs({ ...filterParams, page: 1, per_page: 25 }),
        fetchEmailLogStats(filterParams),
      ]);
      const payload = listRes?.email_logs || {};
      setRows(Array.isArray(payload.data) ? payload.data : []);
      setLastPage(payload.last_page || 1);
      setTotal(payload.total || 0);
      setStats(statsRes?.stats || stats);
      if (detailOpen && detail?.email_log_id === row.email_log_id) {
        const refreshed = await fetchEmailLogDetail(row.email_log_id, org.organization_id);
        setDetail(refreshed?.email_log || null);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Retry failed");
    } finally {
      setRetryingId(null);
    }
  };

  const onExport = async (format) => {
    if (!org?.organization_id) return;
    try {
      const response = await exportEmailLogs(filterParams, format);
      const blob = new Blob([response.data], {
        type: format === "xlsx"
          ? "application/vnd.ms-excel"
          : "text/csv;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `email-logs.${format === "xlsx" ? "xls" : "csv"}`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (error) {
      toast.error(error?.response?.data?.error || "Export failed");
    }
  };

  const statCards = [
    { label: "Total Emails", value: stats.total, icon: <Mail size={20} />, tone: "" },
    { label: "Successfully Sent", value: stats.sent, icon: <MailCheck size={20} />, tone: "ok", valueTone: "ok" },
    { label: "Delivered", value: stats.delivered, icon: <CheckCircle2 size={20} />, tone: "ok", valueTone: "ok" },
    { label: "Failed", value: stats.failed, icon: <MailWarning size={20} />, tone: "bad", valueTone: "bad" },
    { label: "Pending", value: stats.pending, icon: <Clock3 size={20} />, tone: "warn" },
  ];

  return (
    <div className="ems-dashboard ems-fade-up">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">Email Logs</h1>
          <div className="ems-page-sub">
            Outgoing transactional emails for this organization.
          </div>
        </div>
        <div className="ems-head-actions">
          <button type="button" className="ems-btn ems-sm" onClick={() => onExport("csv")}>
            Export CSV
          </button>
          <button type="button" className="ems-btn ems-sm" onClick={() => onExport("xlsx")}>
            Export Excel
          </button>
        </div>
      </div>

      <section className="ems-institute-overview ems-stat-cards-brand" style={{ margin: "12px 0 16px" }}>
        <div className="ems-grid ems-g-5">
          {statCards.map((card) => (
            <div key={card.label} className="ems-card ems-stat">
              <div className="ems-stat-top">
                <div className={`ems-stat-icon${card.tone ? ` ${card.tone}` : ""}`}>
                  {card.icon}
                </div>
              </div>
              <div className="ems-stat-label">{card.label}</div>
              <div className={`ems-stat-value ems-tnum${card.valueTone ? ` ${card.valueTone}` : ""}`}>
                {card.value ?? 0}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="ems-table-toolbar" style={{ display: "flex", gap: 12, alignItems: "center", margin: "12px 0", flexWrap: "wrap" }}>
        <form
          className="ems-search"
          style={{ flex: 1, maxWidth: 360 }}
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setAppliedSearch(search.trim());
          }}
        >
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search recipient or subject"
          />
        </form>
        <SelectFilter
          emptyLabel="All types"
          value={emailType}
          options={TYPE_OPTIONS}
          onChange={(next) => {
            setPage(1);
            setEmailType(next);
          }}
        />
        <SelectFilter
          emptyLabel="All statuses"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(next) => {
            setPage(1);
            setStatus(next);
          }}
        />
        <SelectFilter
          emptyLabel="Triggered by"
          value={triggeredBy}
          options={TRIGGER_OPTIONS}
          onChange={(next) => {
            setPage(1);
            setTriggeredBy(next);
          }}
        />
        <SelectFilter
          emptyLabel="All modules"
          value={relatedModule}
          options={MODULE_OPTIONS}
          onChange={(next) => {
            setPage(1);
            setRelatedModule(next);
          }}
        />
        <button
          type="button"
          className={`ems-btn ems-sm${status === "failed" ? "" : " ems-ghost"}`}
          onClick={() => {
            setPage(1);
            setStatus((current) => (current === "failed" ? "" : "failed"));
          }}
        >
          Failed only
        </button>
        <label style={dateFieldStyle}>
          <span className="ems-page-sub">From</span>
          <input
            type="datetime-local"
            aria-label="From"
            value={from}
            onChange={(event) => {
              setPage(1);
              setFrom(event.target.value);
            }}
            style={dateInputStyle}
          />
        </label>
        <label style={dateFieldStyle}>
          <span className="ems-page-sub">To</span>
          <input
            type="datetime-local"
            aria-label="To"
            value={to}
            onChange={(event) => {
              setPage(1);
              setTo(event.target.value);
            }}
            style={dateInputStyle}
          />
        </label>
      </div>

      <div className="ems-card ems-table-card">
        <div className="ems-table-wrap">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Recipient</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Email Type</TableCell>
                <TableCell>Triggered By</TableCell>
                <TableCell>Sent At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7}>Loading email logs…</TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>No email logs match these filters.</TableCell>
                </TableRow>
              )}
              {!loading &&
                rows.map((row) => {
                  const when = formatWhen(row.sent_at || row.created_at);
                  return (
                    <TableRow key={row.email_log_id}>
                      <TableCell style={{ verticalAlign: "top" }}>
                        <div>{row.recipient_name || "—"}</div>
                        <div className="ems-page-sub">{row.recipient_email}</div>
                      </TableCell>
                      <TableCell style={{ verticalAlign: "top", maxWidth: 280 }}>{row.subject || "—"}</TableCell>
                      <TableCell style={{ verticalAlign: "top" }}>{typeLabel(row.email_type)}</TableCell>
                      <TableCell style={{ verticalAlign: "top" }}>
                        <div style={{ textTransform: "capitalize" }}>{row.triggered_by_type || "system"}</div>
                        {row.triggered_by_name ? (
                          <div className="ems-page-sub">{row.triggered_by_name}</div>
                        ) : null}
                      </TableCell>
                      <TableCell style={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                        <div>{when.date}</div>
                        {when.time ? <div className="ems-page-sub">{when.time}</div> : null}
                      </TableCell>
                      <TableCell style={{ verticalAlign: "top" }}>
                        <span className={`ems-badge ${statusBadge(row.status)}`} style={{ textTransform: "capitalize" }}>
                          {row.status}
                        </span>
                      </TableCell>
                      <TableCell style={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                        <button type="button" className="ems-btn ems-sm" onClick={() => openDetail(row)}>
                          View Details
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
        <span className="ems-page-sub">{total} entries</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="ems-btn ems-sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </button>
          <span className="ems-page-sub">
            Page {page} of {Math.max(1, lastPage)}
          </span>
          <button
            type="button"
            className="ems-btn ems-sm"
            disabled={page >= lastPage || loading}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
          <span className="ems-page-title" style={{ fontSize: 20, margin: 0 }}>Email details</span>
          <IconButton aria-label="Close" onClick={() => setDetailOpen(false)}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && <div className="ems-page-sub">Loading…</div>}
          {!detailLoading && detail && (
            <div style={{ display: "grid", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Meta label="Recipient" value={`${detail.recipient_name || "—"} <${detail.recipient_email}>`} />
                <Meta label="Sender" value={detail.sender_email || "—"} />
                <Meta label="Subject" value={detail.subject || "—"} />
                <Meta label="Category" value={typeLabel(detail.email_type)} />
                <Meta
                  label="Triggered By"
                  value={`${detail.triggered_by_type || "system"}${detail.triggered_by_name ? ` · ${detail.triggered_by_name}` : ""}`}
                />
                <Meta
                  label="Sent At"
                  value={
                    formatWhen(detail.sent_at || detail.created_at).date
                    + (formatWhen(detail.sent_at || detail.created_at).time
                      ? ` ${formatWhen(detail.sent_at || detail.created_at).time}`
                      : "")
                  }
                />
                <Meta label="Status" value={detail.status} />
                <Meta label="Provider" value={`${detail.email_provider || "—"} · ${detail.provider_message_id || "—"}`} />
                <Meta label="Related" value={`${detail.related_module || "—"} ${detail.related_record_id ? `#${detail.related_record_id}` : ""}`} />
                <Meta label="Retries" value={String(detail.retry_count ?? 0)} />
              </div>
              {detail.error_message ? (
                <div>
                  <div className="ems-stat-label" style={{ marginTop: 0 }}>Error</div>
                  <div className="ems-badge red">{detail.error_message}</div>
                </div>
              ) : null}
              {Array.isArray(detail.retry_history) && detail.retry_history.length > 0 ? (
                <div>
                  <div className="ems-stat-label" style={{ marginTop: 0, marginBottom: 6 }}>Retry history</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {detail.retry_history.map((entry, index) => (
                      <li key={`${entry.at}-${index}`}>
                        {entry.at || "—"} · {entry.result || "—"}
                        {entry.error ? ` · ${entry.error}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div>
                <div className="ems-stat-label" style={{ marginTop: 0, marginBottom: 6 }}>Email content</div>
                <iframe
                  title="Email HTML preview"
                  sandbox=""
                  srcDoc={detail.body_html || "<p>No content stored.</p>"}
                  style={{ width: "100%", minHeight: 360, border: "1px solid var(--ems-border, #e5e7eb)", borderRadius: 8, background: "#fff" }}
                />
              </div>
              {canRetry && detail.retryable ? (
                <div>
                  <button
                    type="button"
                    className="ems-btn"
                    disabled={retryingId === detail.email_log_id}
                    onClick={() => onRetry(detail)}
                  >
                    {retryingId === detail.email_log_id ? "Retrying…" : "Retry email"}
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="ems-stat-label" style={{ marginTop: 0 }}>{label}</div>
      <div>{value || "—"}</div>
    </div>
  );
}

export default EmailLogList;
